import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';

// Helper: format date as dd/mm/yyyy
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Helper: get value from Map or Object
const getValue = (data, key) => {
  if (data instanceof Map) return data.get(key);
  return data[key];
};

// Load an image URL as base64 data URL (needed for jsPDF addImage)
const getBase64Image = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

// Brand colours
const MAROON = [142, 44, 44];
const NAVY   = [31, 60, 136];
const GOLD   = [212, 177, 90];

// Records saved before the subType field existed have subType = null — treat as 'Publication'.
const SUBTYPE_LABELS = { paper: 'Paper', poster: 'Poster', publication: 'Publication' };
const subTypeLabel = (subType) => SUBTYPE_LABELS[subType] || 'Publication';

// Fixed 6-month buckets used by dissertations submitted before the flexible
// progress-report system existed — kept only to render whatever old data exists.
const PERIOD_LABELS_LEGACY = {
  '0-6': 'Month 0-6', '6-12': 'Month 6-12', '12-18': 'Month 12-18',
  '18-24': 'Month 18-24', '24-30': 'Month 24-30', '30-36': 'Month 30-36'
};
const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatFlexPeriod = (r) => `${(r.months || []).map(m => MONTH_NAMES_SHORT[m - 1]).join(', ')} ${r.year}`;

// ─────────────────────────────────────────────
// COVER PAGE
// ─────────────────────────────────────────────
const addCoverPage = (doc, studentName, courseName, collegeInfo, studentInfo) => {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // Top maroon banner
  doc.setFillColor(...MAROON);
  doc.rect(0, 0, W, 58, 'F');

  // Logo centred in banner
  if (collegeInfo.logoBase64) {
    try {
      doc.addImage(collegeInfo.logoBase64, 'JPEG', W / 2 - 18, 5, 36, 36);
    } catch (e) { /* skip if image fails */ }
  }

  // College name below logo inside banner
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const collegeNameLines = doc.splitTextToSize(collegeInfo.collegeName || '', W - 30);
  doc.text(collegeNameLines, W / 2, 46, { align: 'center' });

  // Gold divider
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(1.5);
  doc.line(15, 62, W - 15, 62);

  // E-LOGBOOK title
  doc.setTextColor(...MAROON);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(30);
  doc.text('E - LOGBOOK', W / 2, 82, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text('Clinical Competency Record', W / 2, 92, { align: 'center' });

  // Thin gold line
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(40, 99, W - 40, 99);

  // Student details box
  const boxY = 107;
  const boxH = 82;
  doc.setFillColor(248, 249, 250);
  doc.roundedRect(15, boxY, W - 30, boxH, 4, 4, 'F');
  doc.setDrawColor(...MAROON);
  doc.setLineWidth(0.5);
  doc.roundedRect(15, boxY, W - 30, boxH, 4, 4, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('STUDENT DETAILS', W / 2, boxY + 9, { align: 'center' });

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.4);
  doc.line(25, boxY + 13, W - 25, boxY + 13);

  const fields = [
    ['Student Name', studentName],
    ['Course', courseName],
  ];
  if (studentInfo?.profile?.rollNumber) fields.push(['Roll Number', studentInfo.profile.rollNumber]);
  if (studentInfo?.profile?.batch)       fields.push(['Batch',       studentInfo.profile.batch]);
  if (studentInfo?.profile?.year)        fields.push(['Year',        studentInfo.profile.year]);
  if (studentInfo?.email)                fields.push(['Email',       studentInfo.email]);
  fields.push([
    'Generated On',
    new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
  ]);

  const labelX  = 28;
  const valueX  = labelX + 48;
  let   infoY   = boxY + 22;
  const lineGap = 10;

  fields.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(70, 70, 70);
    doc.text(`${label}:`, labelX, infoY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(20, 20, 20);
    doc.text(String(value || 'N/A'), valueX, infoY);
    infoY += lineGap;
  });

  // Bottom maroon footer
  doc.setFillColor(...MAROON);
  doc.rect(0, H - 22, W, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(
    `${collegeInfo.appName || 'E-Logbook System'}  |  © ${collegeInfo.copyrightYear || new Date().getFullYear()}`,
    W / 2, H - 12, { align: 'center' }
  );
  doc.text(collegeInfo.collegeName || '', W / 2, H - 6, { align: 'center' });
};

// ─────────────────────────────────────────────
// BACK COVER PAGE
// ─────────────────────────────────────────────
const addEndPage = (doc, collegeInfo) => {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // Full maroon background
  doc.setFillColor(...MAROON);
  doc.rect(0, 0, W, H, 'F');

  // Gold top border strip
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 4, 'F');

  // Gold bottom border strip
  doc.setFillColor(...GOLD);
  doc.rect(0, H - 4, W, 4, 'F');

  // Centred logo
  if (collegeInfo.logoBase64) {
    try {
      doc.addImage(collegeInfo.logoBase64, 'JPEG', W / 2 - 22, H / 2 - 68, 44, 44);
    } catch (e) { /* skip */ }
  }

  // Gold divider above college name
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(30, H / 2 - 18, W - 30, H / 2 - 18);

  // College name
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const nameLines = doc.splitTextToSize(collegeInfo.collegeName || '', W - 40);
  doc.text(nameLines, W / 2, H / 2 - 10, { align: 'center' });

  // Gold divider below college name
  const afterNameY = H / 2 - 10 + nameLines.length * 7;
  doc.setDrawColor(...GOLD);
  doc.line(30, afterNameY, W - 30, afterNameY);

  // App name
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GOLD);
  doc.text(collegeInfo.appName || 'E-Logbook System', W / 2, afterNameY + 10, { align: 'center' });

  // Copyright at bottom
  doc.setFontSize(8);
  doc.setTextColor(180, 140, 140);
  doc.text(
    `© ${collegeInfo.copyrightYear || new Date().getFullYear()} ${collegeInfo.shortName || ''}`,
    W / 2, H - 10, { align: 'center' }
  );
};

// ─────────────────────────────────────────────
// Content page header (drawn on every module page)
// ─────────────────────────────────────────────
const drawContentPageHeader = (doc, moduleName, studentName, courseName, collegeInfo) => {
  const W = doc.internal.pageSize.getWidth();

  // Thin maroon top bar
  doc.setFillColor(...MAROON);
  doc.rect(0, 0, W, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text(collegeInfo.collegeName || '', W / 2, 7, { align: 'center' });

  // Module title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text(moduleName.toUpperCase(), W / 2, 18, { align: 'center' });

  // Student / course info
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  doc.text(`Student: ${studentName}`, 12, 25);
  doc.text(`Course: ${courseName}`, 12, 30);

  // Gold divider
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(12, 33, W - 12, 33);

  return 37; // starting Y for the table
};

// ─────────────────────────────────────────────
// Column-wise table (entries type)
// ─────────────────────────────────────────────
const generateColumnWiseTable = (doc, module, entries, yPosition, studentName, courseName, collegeInfo, includeSubmissionDate = true) => {
  const W = doc.internal.pageSize.getWidth();
  const tableColumn = ['S.No', ...(includeSubmissionDate ? ['Date'] : []), ...module.fields.map(f => f.label), 'Verified by Faculty'];
  const tableRows = [];

  entries.forEach((entry, index) => {
    const entryData = entry.entries && entry.entries[0] ? entry.entries[0].data : {};

    let verifiedBy = '';
    if (entry.review && entry.review.reviewerName && entry.status === 'approved') {
      verifiedBy = entry.review.reviewerName;
    }

    tableRows.push([
      index + 1,
      ...(includeSubmissionDate ? [formatDate(entry.createdAt)] : []),
      ...module.fields.map(field => getValue(entryData, field.key) || ''),
      verifiedBy
    ]);
  });

  // Pad to 30 rows
  for (let i = entries.length; i < 30; i++) {
    tableRows.push(new Array(tableColumn.length).fill(''));
  }

  autoTable(doc, {
    head: [tableColumn],
    body: tableRows,
    startY: yPosition,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.5 },
    headStyles: { fillColor: MAROON, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center' },
      ...(includeSubmissionDate ? { 1: { cellWidth: 22 } } : {}),
      [tableColumn.length - 1]: { cellWidth: 32, halign: 'left' }
    },
    margin: { top: 38, left: 12, right: 12 },
    didDrawPage: () => {
      drawContentPageHeader(doc, module.name, studentName, courseName, collegeInfo);
      const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
      const total = doc.internal.getNumberOfPages();
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text(`Page ${pageNumber} of ${total}`, W / 2, doc.internal.pageSize.getHeight() - 6, { align: 'center' });
    }
  });
};

// ─────────────────────────────────────────────
// Row-wise table (counters type)
// ─────────────────────────────────────────────
const generateRowWiseTable = (doc, module, entries, yPosition, studentName, courseName, collegeInfo) => {
  const W = doc.internal.pageSize.getWidth();
  const maxColumns = 6;
  const tableColumn = ['Field', ...Array.from({ length: maxColumns }, (_, i) => (i + 1).toString())];
  const tableRows = [];

  module.fields.forEach((field) => {
    const row = [field.label];
    for (let i = 0; i < maxColumns; i++) {
      if (entries[i]) {
        const value = getValue(entries[i].counters, field.key);
        row.push(value !== undefined && value !== null ? value.toString() : '');
      } else {
        row.push('');
      }
    }
    tableRows.push(row);
  });

  while (tableRows.length < 25) {
    tableRows.push(new Array(tableColumn.length).fill(''));
  }

  autoTable(doc, {
    head: [tableColumn],
    body: tableRows,
    startY: yPosition,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.5, halign: 'center' },
    headStyles: { fillColor: MAROON, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    columnStyles: { 0: { cellWidth: 60, halign: 'left' } },
    margin: { top: 38, left: 12, right: 12 },
    didDrawPage: () => {
      drawContentPageHeader(doc, module.name, studentName, courseName, collegeInfo);
      const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
      const total = doc.internal.getNumberOfPages();
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text(`Page ${pageNumber} of ${total}`, W / 2, doc.internal.pageSize.getHeight() - 6, { align: 'center' });
    }
  });
};

// ─────────────────────────────────────────────
// ACHIEVEMENTS PAGE (Awards or Publications)
// ─────────────────────────────────────────────
const addAchievementsSection = (doc, sectionTitle, items, studentName, courseName, collegeInfo, includeType = false) => {
  const W = doc.internal.pageSize.getWidth();

  doc.addPage('a4', 'portrait');
  let y = drawContentPageHeader(doc, sectionTitle, studentName, courseName, collegeInfo);

  const head = includeType ? ['S.No', 'Type', 'Title', 'Description'] : ['S.No', 'Title', 'Description'];
  const columnStyles = includeType
    ? { 0: { cellWidth: 12, halign: 'center' }, 1: { cellWidth: 24, halign: 'center' }, 2: { cellWidth: 48 }, 3: { cellWidth: 'auto' } }
    : { 0: { cellWidth: 12, halign: 'center' }, 1: { cellWidth: 55 }, 2: { cellWidth: 'auto' } };

  autoTable(doc, {
    head: [head],
    body: items.map((item, i) => [
      i + 1,
      ...(includeType ? [subTypeLabel(item.subType)] : []),
      item.title || '',
      item.description || '',
    ]),
    startY: y,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.5, overflow: 'linebreak' },
    headStyles: { fillColor: MAROON, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
    columnStyles,
    margin: { top: 38, left: 12, right: 12 },
    didDrawPage: () => {
      drawContentPageHeader(doc, sectionTitle, studentName, courseName, collegeInfo);
      const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
      const total = doc.internal.getNumberOfPages();
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text(`Page ${pageNumber} of ${total}`, W / 2, doc.internal.pageSize.getHeight() - 6, { align: 'center' });
    },
  });
};

// ─────────────────────────────────────────────
// DISSERTATION / THESIS PAGE
// ─────────────────────────────────────────────
const addDissertationSection = (doc, dissertation, studentName, courseName, collegeInfo) => {
  const W = doc.internal.pageSize.getWidth();
  const sectionTitle = 'Thesis / Dissertation';

  doc.addPage('a4', 'portrait');
  let y = drawContentPageHeader(doc, sectionTitle, studentName, courseName, collegeInfo);

  // ── Main details box ──
  const details = [
    ['Topic / Title', dissertation.topicName || 'N/A'],
    ['PG Teacher(s)', (dissertation.pgTeacherNames || []).join(', ') || 'N/A'],
    ['Approval Date', formatDate(dissertation.approvalDate)],
    ['Submission Date', formatDate(dissertation.submissionDate)],
    ['Status', dissertation.isCompleted ? 'Completed' : 'In Progress'],
  ];

  autoTable(doc, {
    body: details,
    startY: y,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.3 },
    columnStyles: {
      0: { cellWidth: 44, fontStyle: 'bold', textColor: [70, 70, 70] },
      1: { cellWidth: 'auto', textColor: [20, 20, 20] },
    },
    margin: { left: 12, right: 12 },
    didDrawPage: () => {
      drawContentPageHeader(doc, sectionTitle, studentName, courseName, collegeInfo);
    },
  });

  y = doc.lastAutoTable.finalY + 8;

  // ── Progress reports — combine legacy fixed-period reports with the newer
  // flexible (student-picked year + 1-6 months) reports into one table ──
  const legacyReports = (dissertation.reports || []).map(r => ({
    period: PERIOD_LABELS_LEGACY[r.period] || r.period || '',
    content: r.content,
    isGraded: r.isGraded,
    grade: r.grade,
    gradedByName: r.gradedByName,
    teacherComments: r.teacherComments,
  }));
  const flexReports = [...(dissertation.flexibleReports || [])]
    .sort((a, b) => (a.year - b.year) || (a.months?.[0] - b.months?.[0]))
    .map(r => ({
      period: formatFlexPeriod(r),
      content: r.content,
      isGraded: r.isGraded,
      grade: r.grade,
      gradedByName: r.gradedByName,
      teacherComments: r.teacherComments,
    }));
  const reports = [...legacyReports, ...flexReports];

  if (reports.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...MAROON);
    doc.text('Progress Reports', 12, y);
    y += 4;

    autoTable(doc, {
      head: [['Period', 'Progress / Content', 'Grade\n(0-4)', 'Graded By', 'Comments']],
      body: reports.map(r => [
        r.period || '',
        r.content || '',
        r.isGraded ? String(r.grade ?? '') : 'Pending',
        r.gradedByName || '',
        r.teacherComments || '',
      ]),
      startY: y,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.5, overflow: 'linebreak' },
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center' },
      columnStyles: {
        0: { cellWidth: 26, halign: 'center' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 16, halign: 'center' },
        3: { cellWidth: 36 },
        4: { cellWidth: 40 },
      },
      margin: { top: 38, left: 12, right: 12 },
      didDrawPage: () => {
        drawContentPageHeader(doc, sectionTitle, studentName, courseName, collegeInfo);
        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
        const total = doc.internal.getNumberOfPages();
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text(`Page ${pageNumber} of ${total}`, W / 2, doc.internal.pageSize.getHeight() - 6, { align: 'center' });
      },
    });
  }
};

// ─────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────
export const generateLogbookPDF = async (
  studentName,
  courseName,
  modules,
  allEntries,
  collegeInfo = {},
  studentInfo = {},
  extras = {}
) => {
  // Pre-load college logo as base64
  if (collegeInfo.logo) {
    collegeInfo.logoBase64 = await getBase64Image(collegeInfo.logo);
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // Page 1: Cover
  addCoverPage(doc, studentName, courseName, collegeInfo, studentInfo);

  // Group entries by module
  const entriesByModule = {};
  allEntries.forEach(entry => {
    const id = entry.moduleId;
    if (!entriesByModule[id]) entriesByModule[id] = [];
    entriesByModule[id].push(entry);
  });

  // Content pages (one per module with entries). Modules with many field
  // columns don't fit legibly on a portrait page — render those in landscape.
  const includeSubmissionDate = extras.includeSubmissionDate !== false;
  modules.forEach((module) => {
    const moduleEntries = entriesByModule[module.moduleId] || [];
    if (moduleEntries.length === 0) return;

    const totalColumns = module.type === 'entries'
      ? module.fields.length + 2 + (includeSubmissionDate ? 1 : 0) // S.No + fields + Verified (+ Date)
      : 0;
    const useLandscape = module.type === 'entries' && totalColumns > 6;

    doc.addPage('a4', useLandscape ? 'landscape' : 'portrait');
    const startY = drawContentPageHeader(doc, module.name, studentName, courseName, collegeInfo);

    if (module.type === 'entries') {
      generateColumnWiseTable(doc, module, moduleEntries, startY, studentName, courseName, collegeInfo, includeSubmissionDate);
    } else if (module.type === 'counters') {
      generateRowWiseTable(doc, module, moduleEntries, startY, studentName, courseName, collegeInfo);
    }
  });

  // Awards section
  const awards = (extras.achievements || []).filter(a => a.type === 'award');
  if (awards.length > 0) {
    addAchievementsSection(doc, 'Awards', awards, studentName, courseName, collegeInfo);
  }

  // Publications section (Paper / Poster / Publication)
  const publications = (extras.achievements || []).filter(a => a.type === 'publication');
  if (publications.length > 0) {
    addAchievementsSection(doc, 'Paper / Poster / Publication', publications, studentName, courseName, collegeInfo, true);
  }

  // Dissertation section
  if (extras.dissertation) {
    addDissertationSection(doc, extras.dissertation, studentName, courseName, collegeInfo);
  }

  // Last page: Back cover
  doc.addPage('a4', 'portrait');
  addEndPage(doc, collegeInfo);

  // Save
  const fileName = `${studentName.replace(/\s+/g, '_')}_Logbook_${new Date().getTime()}.pdf`;

  if (Capacitor.isNativePlatform()) {
    const base64Data = doc.output('datauristring').split(',')[1];
    const result = await Filesystem.writeFile({
      path: fileName,
      data: base64Data,
      directory: Directory.Documents,
      recursive: true,
    });
    try {
      await FileOpener.open({
        filePath: result.uri,
        contentType: 'application/pdf',
      });
    } catch (e) {
      alert(`PDF saved successfully:\n${fileName}\n\nOpen it from your Files app.`);
    }
  } else {
    doc.save(fileName);
  }
};

export default generateLogbookPDF;
